import { useEffect, useRef, useState } from 'react';
import { usePhotoUploads } from '@/components/PhotoUploads';
import { useRowboat } from '@/rowboat';
import { cacheIdentifyImage } from '@/services/identifyImages';
import { getIdentifyQueue, type IdentifyQueue } from '@/services/identifyQueue';
import {
  archivePhoto,
  createPhotoRow,
  nextSortOrder,
  photosForItem,
} from '@/services/photoService';

const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

export function usePhotoCapture({
  itemId,
  groupId,
  author,
  identifyQueue,
}: {
  itemId: string;
  groupId: string;
  author: string;
  identifyQueue?: Pick<IdentifyQueue, 'touch'>;
}) {
  const graph = useRowboat();
  const { enqueue } = usePhotoUploads();
  const [error, setError] = useState('');
  // Full-size previews by photo id, owned by the open item: a new tile shows one before its
  // bytes reach IndexedDB, and closing the item revokes them so camera blobs are not kept.
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  // Ids of the rows this item's captures created and enqueued, in save order.
  const [saved, setSaved] = useState<string[]>([]);

  useEffect(() => {
    return () => {
      for (const url of Object.values(previewsRef.current)) URL.revokeObjectURL?.(url);
    };
  }, []);

  const addRow = () =>
    createPhotoRow(graph, {
      itemId,
      groupId,
      author,
      role: photosForItem(graph, itemId).length === 0 ? 'overall' : '',
      sortOrder: nextSortOrder(graph, itemId),
    });

  // One save at a time, so sort_order (read from the item's existing rows) follows shot order.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const savedSinceIdentify = useRef(false);

  const saveOne = async (file: File): Promise<string | undefined> => {
    let photoId: string;
    try {
      photoId = await addRow();
    } catch (e) {
      return message(e, 'Could not add the photo');
    }
    cacheIdentifyImage(photoId, file);
    let url: string | undefined;
    try {
      const preview = URL.createObjectURL(file);
      url = preview;
      setPreviews((prev) => ({ ...prev, [photoId]: preview }));
      await enqueue(photoId, file);
    } catch (e) {
      if (url) URL.revokeObjectURL?.(url);
      setPreviews((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([id]) => id !== photoId)),
      );
      const failure = message(e, 'Photo enqueue failed');
      const removal = await archivePhoto(graph, photoId).then(
        () => undefined,
        (err: unknown) => message(err, 'Could not remove the failed photo'),
      );
      return removal ?? failure;
    }
    savedSinceIdentify.current = true;
    setSaved((prev) => [...prev, photoId]);
    return undefined;
  };

  const saveAll = async (files: File[]) => {
    const failures: string[] = [];
    for (const file of files) {
      const failure = await saveOne(file);
      if (failure) failures.push(failure);
    }
    if (failures.length === 0) setError('');
    else if (files.length === 1) setError(failures[0]);
    else setError(`${failures.length} of ${files.length} photos could not be saved`);
  };

  const enqueueStep = (step: () => Promise<void>) => {
    const run = queue.current.then(step);
    // A rejected save must not leave every later save chained onto it.
    queue.current = run.catch(() => {});
    return run;
  };

  const takePhotos = (files: File[]) => enqueueStep(() => saveAll(files));

  // Queued behind the saves so the runner finds every row they create.
  const identify = () =>
    enqueueStep(async () => {
      if (!savedSinceIdentify.current) return;
      savedSinceIdentify.current = false;
      await (identifyQueue ?? getIdentifyQueue())
        .touch(itemId)
        .catch((err) => console.warn('identify touch failed', itemId, err));
    });

  return { takePhotos, identify, previews, saved, error };
}
